import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { skip } from 'rxjs/operators';

import { CelumPropertiesProvider } from '@celum/core/configuration';
import { LocalizedValue } from '@celum/core/model';
import { DateService } from '@celum/ng2base/util';

import { evaluateLocalizedValue, instantTranslate, InstantTranslateOptions } from './translate-instant';
import { currentLocaleStream } from './translation-util';

@Injectable({ providedIn: 'root' })
export class TranslationHelper {
  private static nativeTranslations: LocalizedValue = {
    en: 'English',
    de: 'Deutsch',
    fr: 'Français',
    it: 'Italiano',
    es: 'Español',
    ja: '日本語'
  };
  private availableLanguages: string[];

  constructor(private translate: TranslateService) {}

  public get locale(): string {
    return this.translate.getCurrentLang();
  }

  public get browserLocale(): string {
    return this.translate.getBrowserLang();
  }

  public init(useLanguage?: string, availableLanguages?: string[], defaultLanguage?: string): void {
    this.availableLanguages = availableLanguages || CelumPropertiesProvider.properties.availableLanguages;

    let defaultLang = defaultLanguage || CelumPropertiesProvider.properties.defaultLanguage;
    if (!this.availableLanguages.includes(defaultLang)) {
      defaultLang = this.availableLanguages[0];
    }
    this.translate.setFallbackLang(defaultLang);

    if (useLanguage && this.availableLanguages.includes(useLanguage)) {
      this.setLanguage(useLanguage);
    } else {
      // set default language
      this.translate.use(this.translate.getFallbackLang());
      DateService.GLOBAL_LOCALE = this.locale;
    }

    this.translate.onLangChange.pipe(skip(1)).subscribe(event => {
      DateService.GLOBAL_LOCALE = event.lang;
    });
  }

  public setLanguage(newLanguage: string): void {
    if (!this.availableLanguages) {
      console.warn(
        'TranslationHelper: setLanguage called before init() - ignoring. In a Nova extension, make sure getBaseProviders receives the ' +
          "extension's TranslationHelper class."
      );
      return;
    }

    // remove cultural codes
    const newLang = newLanguage.split('-')[0];
    const locale = this.availableLanguages.find(language => language === newLang);

    if (locale && locale === this.translate.getCurrentLang()) {
      console.log('TranslationHelper: already using language ' + locale);
      DateService.GLOBAL_LOCALE = locale;
    } else if (locale) {
      console.log(`TranslationHelper: changing UI language from ${this.locale} to ${locale}`);
      this.translate.use(locale);
      DateService.GLOBAL_LOCALE = locale;
    } else {
      const defaultLang = this.translate.getFallbackLang();
      console.log(`TranslationHelper: selected locale ${newLang} not available! Using default locale: ${defaultLang}`);

      if (this.locale !== defaultLang) {
        console.log(`TranslationHelper: changing UI language from ${this.locale} to default locale: ${defaultLang}`);
        this.translate.use(defaultLang);
        DateService.GLOBAL_LOCALE = defaultLang;
      }
    }
  }

  /**
   * Returns a stream of the current ui - language. If it emits, it's guaranteed that the language files for the new language are already loaded
   */
  public currentLocaleStream(): Observable<string> {
    return currentLocaleStream(this.translate);
  }

  public getDefaultLocale(): string {
    return this.translate.getFallbackLang();
  }

  public instant(key: string | LocalizedValue, options?: InstantTranslateOptions): string;
  public instant(key: string | LocalizedValue): string;
  public instant(
    key: string | LocalizedValue,
    displayLanguage: string,
    defaultLanguage: string,
    targetLanguage?: string,
    useFallbacks?: boolean,
    interpolationParams?: object,
    fallbackLocale?: string,
    useAnyFallback?: boolean
  ): string;
  /**
   * Do an instant translation - this method is synchronous - you are responsible for knowing when the translations were loaded.
   * You can use the currentLocaleStream to find out when languages were loaded
   * @param key message-key or localized value
   * @param displayLanguageOrOptions current ui-language or translate options
   * @param defaultLanguage default language (used as a fallback)
   * @param targetLanguage translate to specific language - if this is set - it will not fallback to other languages
   * @param useFallbacks Specify if translation should fall back to other languages if there is no translation for wanted one
   * @param interpolationParams which will be used if string is passed as string
   * @param fallbackLocale - The locale to use as a fallback when the specified language key does not have a value.
   * @param useAnyFallback if true, it will return the first available translation if there is no translation for the specified language and fallbacks
   */
  public instant(
    key: string | LocalizedValue,
    displayLanguageOrOptions?: string | InstantTranslateOptions,
    defaultLanguage?: string,
    targetLanguage?: string,
    useFallbacks?: boolean,
    interpolationParams?: object,
    fallbackLocale?: string,
    useAnyFallback?: boolean
  ): string {
    return instantTranslate(
      this.translate,
      key,
      displayLanguageOrOptions as string, // slight hack to keep logic on if its an object call or arg call within the instantTranslate
      defaultLanguage,
      targetLanguage,
      useFallbacks,
      interpolationParams,
      fallbackLocale,
      useAnyFallback
    );
  }

  public static evaluateLocalizedValue(
    input: LocalizedValue,
    languageParam: string,
    displayLanguage: string,
    defaultLanguage: string,
    useFallbacks = true,
    fallbackLocale?: string,
    useAnyFallback?: boolean
  ): string {
    return evaluateLocalizedValue(input, languageParam, displayLanguage, defaultLanguage, useFallbacks, fallbackLocale, useAnyFallback);
  }

  public static getUILocalesNativeTranslations(locales: string[]): LocalizedValue {
    return locales.reduce((obj, value) => ({ ...obj, [value]: this.nativeTranslations[value] }), {});
  }
}
